import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { Provider as ModalProvider } from 'ui-components/components/modal/context';
import GlobalEventListenerRoot from 'ui-components/components/global-event-listener/root';
import useMaxZ from 'ui-components/hooks/services/use-max-z';
import testid from 'ui-components/services/testid-service';

import CloseModalHotkey from '../hotkeys/close-modal';
import ConfirmDialogueModal from '../modals/confirm-dialogue-modal';
import { useModal } from '../../hooks/use-modal';
import { useModalManagerPortal } from '../../portals/hooks';
import { MessageDescriptorPropType } from '../../prop-types/intl';

import style from './index.less';

const isCancel = true;
const ModalPortal = ({
  children,
  confirmCancelBodyMessage,
  confirmCancelButtonMessage,
  confirmCancelHeaderMessage,
  onCancel,
  onClose,
  hideCloseButton = false,
  shouldConfirmOnClose = null,
}) => {
  const zIndex = useMaxZ();
  const zIndexStyle = useMemo(() => ({ zIndex }), [zIndex]);
  const modalManagerPortalRef = useModalManagerPortal();

  const { open: onOpenConfirmModal, close: onCloseConfirmModal, isOpen: isConfirmModalOpen } = useModal();

  const onCancelCb = useCallback(() => {
    const hasConfirmMessages = confirmCancelBodyMessage && confirmCancelButtonMessage && confirmCancelHeaderMessage;
    const shouldConfirm = shouldConfirmOnClose ? shouldConfirmOnClose() : hasConfirmMessages;

    if (hasConfirmMessages && shouldConfirm) {
      onOpenConfirmModal();
    } else if (onCancel) {
      onCancel();
    } else {
      onClose(isCancel);
    }
  }, [
    confirmCancelBodyMessage,
    confirmCancelButtonMessage,
    confirmCancelHeaderMessage,
    onCancel,
    onOpenConfirmModal,
    onClose,
    shouldConfirmOnClose,
  ]);

  /**
   * This is dumb because since we provide onClose to the ModalProvider a developer may call something like
   * `const { onClose } = useModalContext();` followed by a `<button onClick={onClose}>Close</button>`
   * which ultimately will feed the onClose event with a synthetic event as the param. So in this case we filter out the
   * boolean and events so we may capture the appropriate booleans.
   * TODO: Once we remove modalFrameworkMethods and standardize our callback convention within modals to do away with this bandaid func.
   * @type {(function(*): void)|*}
   */
  const onCloseCb = useCallback(
    (eventOrBool) => {
      if (typeof eventOrBool === 'boolean') {
        onClose(eventOrBool);
      } else {
        const cancelBandaid = false;
        onClose(cancelBandaid);
      }
    },
    [onClose],
  );

  if (!modalManagerPortalRef) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={style.modal} {...testid('component-modal-portal')}>
      <div className={style['modal-background']} style={zIndexStyle} />
      <GlobalEventListenerRoot>
        <CloseModalHotkey onClose={hideCloseButton ? undefined : onCancelCb}>
          <div className={style['modal-positioning-wrapper']} style={zIndexStyle}>
            <div className={style['modal-component-wrapper']}>
              <div
                className={style['modal-positioning-wrapper-background']}
                onClick={hideCloseButton ? undefined : onCancelCb}
                {...testid('modal-background')}
              />
              <ModalProvider onCancel={hideCloseButton ? undefined : onCancelCb} onClose={hideCloseButton ? undefined : onCloseCb}>
                {children}
              </ModalProvider>
            </div>
          </div>
        </CloseModalHotkey>
      </GlobalEventListenerRoot>
      {isConfirmModalOpen && (
        <ConfirmDialogueModal
          body={
            <FormattedMessage
              {...confirmCancelBodyMessage}
              values={{
                p: (chunks) => <p>{chunks}</p>,
                br: <br />,
              }}
            />
          }
          buttonMessage={<FormattedMessage {...confirmCancelButtonMessage} />}
          header={<FormattedMessage {...confirmCancelHeaderMessage} />}
          onClose={onCloseConfirmModal}
          onConfirm={onCancel}
        />
      )}
    </div>,
    modalManagerPortalRef,
  );
};

ModalPortal.propTypes = {
  children: PropTypes.node.isRequired,
  hideCloseButton: PropTypes.bool,
  onCancel: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  confirmCancelBodyMessage: MessageDescriptorPropType,
  confirmCancelButtonMessage: MessageDescriptorPropType,
  confirmCancelHeaderMessage: MessageDescriptorPropType,
  shouldConfirmOnClose: PropTypes.func,
};

export default ModalPortal;
